import { Flex, FormItemsGroup, InputProps, Spacing } from '@billy/design-system'
import { ReactElement, ReactNode, useCallback } from 'react'
import { UseFormRegisterReturn } from 'react-hook-form'

import { EInvoiceAddressEntityTypeToggle } from './elements/EInvoicingAddressEntityTypeToggle'
import { EInvoiceAddressInputField } from './elements/EInvoicingAddressInputField'
import { EInvoiceAddressSchemeTypeSelectField } from './elements/EInvoicingAddressSchemeTypeSelectField'
import { EInvoiceAddressSchemeType } from './enums/eInvoiceAddressSchemeType'
import { EntityType } from './enums/entityType'
import { EInvoiceScheme } from './types/eInvoiceScheme'
import { getEInvoiceCountryConfig } from './utils/getEInvoiceCountryConfig'

interface EInvoicingAddressProps {
  address?: string
  autoCompleted?: boolean
  countryId?: string
  eInvoiceAddressFormField?: UseFormRegisterReturn
  eInvoiceAddressInputErrorMessage?: string
  entityType?: EntityType
  inputLabel?: ReactNode
  inputPlaceholder?: string
  onEInvoiceAddressEntityTypeChange: (entityType?: EntityType) => void
  onEInvoiceAddressInputChange: (address?: string) => void
  onEInvoiceAddressSchemeTypeChange: (schemeType?: EInvoiceAddressSchemeType) => void
  schemeType: EInvoiceScheme
  size?: InputProps['size']
}

export const EInvoicingAddress = ({
  address,
  autoCompleted,
  countryId,
  eInvoiceAddressFormField,
  eInvoiceAddressInputErrorMessage,
  entityType,
  inputLabel,
  inputPlaceholder,
  onEInvoiceAddressEntityTypeChange,
  onEInvoiceAddressInputChange,
  onEInvoiceAddressSchemeTypeChange,
  schemeType,
  size,
}: EInvoicingAddressProps): ReactElement => {
  const eInvoiceConfig = getEInvoiceCountryConfig(countryId)
  const { isPublicEntityTooltipVisible = false, isSchemeTypeSelectVisible = true } = eInvoiceConfig || {}

  const isPublicEntity = entityType === EntityType.Public

  const shouldDisplayEntityToggle = !!eInvoiceConfig
  const shouldDisplaySchemeTypeSelect = !isPublicEntity && isSchemeTypeSelectVisible
  const shouldDisplayInputFieldLabelTooltip = isPublicEntity && isPublicEntityTooltipVisible
  const isAddressInputFieldShort = shouldDisplaySchemeTypeSelect && shouldDisplayEntityToggle

  const handleEntityTypeChange = useCallback(
    (newEntityType?: EntityType) => {
      onEInvoiceAddressEntityTypeChange(newEntityType)
    },
    [onEInvoiceAddressEntityTypeChange],
  )

  const handleSchemeTypeChange = useCallback(
    (newSchemeType?: EInvoiceAddressSchemeType) => {
      onEInvoiceAddressSchemeTypeChange(newSchemeType)
    },
    [onEInvoiceAddressSchemeTypeChange],
  )

  const handleAddressChange = useCallback(
    (newAddress?: string) => {
      onEInvoiceAddressInputChange(newAddress)
    },
    [onEInvoiceAddressInputChange],
  )

  return (
    <Flex flexDirection="column" gap={Spacing.S8}>
      <FormItemsGroup itemsInRow={24} columnSpacing={Spacing.S8}>
        {shouldDisplayEntityToggle && (
          <EInvoiceAddressEntityTypeToggle entityType={entityType} handleEntityTypeChange={handleEntityTypeChange} />
        )}
        {shouldDisplaySchemeTypeSelect && (
          <EInvoiceAddressSchemeTypeSelectField
            countryId={countryId}
            formItemColumnEnd={shouldDisplayEntityToggle ? 15 : 9}
            formItemColumnStart={shouldDisplayEntityToggle ? 9 : 1}
            handleSchemeTypeChange={handleSchemeTypeChange}
            schemeType={schemeType}
            size={size}
          />
        )}
        <EInvoiceAddressInputField
          address={address}
          autoCompleted={autoCompleted}
          countryId={countryId}
          eInvoiceAddressFormField={eInvoiceAddressFormField}
          eInvoiceAddressInputErrorMessage={eInvoiceAddressInputErrorMessage}
          entityType={entityType}
          formItemColumnEnd={25}
          formItemColumnStart={isAddressInputFieldShort ? 15 : 9}
          inputLabel={inputLabel}
          inputPlaceholder={inputPlaceholder}
          onAddressChange={handleAddressChange}
          schemeType={schemeType}
          shouldDisplayInputFieldLabelTooltip={shouldDisplayInputFieldLabelTooltip}
          size={size}
        />
      </FormItemsGroup>
    </Flex>
  )
}
